import { useState, useCallback, useEffect } from 'react';
import { useAuth } from './useReduxAuth';
import { useNotification } from './useNotification';
import { useTranslation } from 'react-i18next';
import { hasuraClient } from '../utils/graphqlClient';
import {
  GET_COMPANIES_FOR_USER_FORM_QUERY,
  GET_ALL_USERS_QUERY,
} from '../graphql/queries';
import { UPDATE_GROUP_TAGS } from '../graphql/mutations';
import { useDynamicEntity } from './useDynamicEntity';

export const useGroups = ({ paginated = false } = {}) => {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const { showNotification } = useNotification();

  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);

  const isRootUser = currentUser?.role === 'ROOT';

  const dynamicHook = useDynamicEntity({
    entityType: 'groups',
    gqlEntityKey: 'groups',
    queryName: 'GET_ALL_GROUPS_QUERY',
    createMutation: 'CREATE_GROUP_MUTATION',
    updateMutation: 'UPDATE_GROUP_MUTATION',
    deleteMutation: 'DELETE_GROUP_MUTATION',
    // Groups pattern configuration
    mutationPattern: 'groups',
    createDirectVariables: true,
    updateDirectVariables: true,
    updateVariableName: '_set',
    serverPagination: paginated,
    aggregateKey: paginated ? 'groups_aggregate' : null,
    transformData: (group) => ({
      ...group,
      companyName: group.company?.name || 'Unknown',
      managerName: group.user?.first_name 
        ? `${group.user.first_name} ${group.user.last_name}`
        : 'Atanmamış',
    }),
    transformFormData: (formData) => ({
      name: formData.name,
      description: formData.description || null,
      company_id: formData.companyId || currentUser?.companyId,
      group_manager_id: formData.managerId || null,
    }),
    transformFormDataForEdit: (group) => {
      return {
        name: group.name,
        description: group.description,
        companyId: group.company_id,
        managerId: group.group_manager_id,
        tagIds: group.group_tags?.map(gt => gt.tag_id) || [],
      };
    },
    customWhereCondition: isRootUser ? {} : { company_id: { _eq: currentUser?.companyId } },
    onSuccess: async (operation, result, formData) => {
      if (formData.tagIds && (operation === 'create' || operation === 'update')) {
        try {
          const objects = formData.tagIds.map(tagId => ({
            group_id: result.id,
            tag_id: tagId,
          }));
          await hasuraClient.mutate(UPDATE_GROUP_TAGS, {
            group_id: result.id,
            objects: objects,
          });
        } catch (error) {
          console.error('Error updating group tags:', error);
        }
      }
    },
  });

  const fetchCompanies = useCallback(async () => {
    if (!isRootUser) return;
    
    try {
      const data = await hasuraClient.query(GET_COMPANIES_FOR_USER_FORM_QUERY);
      setCompanies(data.companies || []);
    } catch (error) {
      console.error('Error fetching companies:', error);
      showNotification(t('companies.messages.fetchError') || 'Error fetching companies', 'error');
    }
  }, [isRootUser, t, showNotification]);

  const fetchUsers = useCallback(async () => {
    try {
      let data;
      if (isRootUser) {
        data = await hasuraClient.query(GET_ALL_USERS_QUERY);
      } else {
        data = await hasuraClient.query(GET_ALL_USERS_QUERY, {
          where: { company_id: { _eq: currentUser?.companyId } },
        });
      }
      
      const transformedUsers = data.users.map(user => ({
        id: user.id,
        firstName: user.first_name,
        lastName: user.last_name,
        first_name: user.first_name, // Keep original for backward compatibility
        last_name: user.last_name,   // Keep original for backward compatibility
        email: user.email,
        role: user.role,
        companyId: user.company_id,
        companyName: user.company?.name,
        teamId: user.team_id,
        teamName: user.team?.name,
      }));
      
      setUsers(transformedUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
      showNotification(t('users.messages.fetchError') || 'Error fetching users', 'error');
    }
  }, [currentUser?.companyId, isRootUser, t, showNotification]);

  useEffect(() => {
    if (currentUser?.id) {
      fetchCompanies();
      fetchUsers();
    }
  }, [currentUser?.id, currentUser?.role, currentUser?.companyId]);

  return {
    ...dynamicHook,
    companies,
    users,
    isRootUser,
  };
}; 